import { useEffect, useState } from "react"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import {
  ANALYTICS_MANAGE_USERS_SCOPE,
  getValidAccessToken,
  loadTokens,
  tokenHasRequiredAnalyticsScopes,
} from "../utils/ga4Tokens"

export default function GA4Access() {
  const user = useUser()
  const { organizations, selectedOrgId } = useOrganization()
  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)
  const isFreeOrg = selectedOrg?.plan?.name === "free"
  const [updatedAt, setUpdatedAt] = useState(null)
  const [expiresAt, setExpiresAt] = useState(null)

  useEffect(() => {
    const load = async () => {
      if (!user) return
      await getValidAccessToken(supabase, user)
      const data = await loadTokens(supabase, user.id)
      setUpdatedAt(data?.updated_at || null)
      setExpiresAt(data?.expires_at || null)
    }
    load()
  }, [user, selectedOrgId, isFreeOrg])

  const refreshGoogleAuth = async () => {
    let queryParams = { access_type: isFreeOrg ? "online" : "offline" }
    const { data: sessionData } = await supabase.auth.getSession()
    const session = sessionData?.session
    let storedTokens = {}
    if (user) {
      try {
        storedTokens = (await loadTokens(supabase, user.id)) || {}
      } catch (tokenErr) {
        console.warn("[GA4] Unable to load stored GA4 tokens before OAuth", tokenErr)
        storedTokens = {}
      }
    }
    const storedRefreshTokenPresent = Boolean(
      storedTokens &&
        Object.prototype.hasOwnProperty.call(storedTokens, "refresh_token") &&
        storedTokens.refresh_token,
    )
    if (!isFreeOrg && !session?.provider_refresh_token && user) {
      if (!storedRefreshTokenPresent) queryParams.prompt = "consent"
    }
    const storedAccessToken =
      storedTokens && typeof storedTokens.access_token === "string"
        ? storedTokens.access_token
        : null
    const hadStoredTokens = Boolean(
      storedAccessToken || storedRefreshTokenPresent,
    )
    if (hadStoredTokens) {
      const hasScope = storedAccessToken
        ? await tokenHasRequiredAnalyticsScopes(
            storedAccessToken,
            ANALYTICS_MANAGE_USERS_SCOPE,
          )
        : false
      if (!hasScope) {
        console.log(
          "[GA4] Requesting OAuth consent to ensure manage users scope",
          { userId: user?.id || null },
        )
        queryParams.prompt = "consent"
      }
    }
    await supabase.auth
      .signInWithOAuth({
        provider: "google",
        options: {
          scopes:
            "https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/analytics.manage.users.readonly",
          redirectTo: window.location.href,
          skipBrowserRedirect: true,
          queryParams,
        },
      })
      .then(({ data, error }) => {
        if (error) return
        if (data?.url) window.location.assign(data.url)
      })
  }

  return (
    <div className="page-container">
      <h2>GA4 OAuth Access</h2>
      <p>
        {updatedAt
          ? `Token last updated: ${new Date(updatedAt).toLocaleString()}${
              expiresAt ? ` (expires ${new Date(expiresAt).toLocaleString()})` : ""
            }`
          : "No token stored"}
      </p>
      <button onClick={refreshGoogleAuth}>Refresh OAuth Token</button>
    </div>
  )
}

